import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import { useAuth } from './AuthContext';

const SELECTED_ZONE_STORAGE_KEY = 'selectedZone';

const ZoneContext = createContext(null);

function readStoredZone() {
  try {
    const saved = localStorage.getItem(SELECTED_ZONE_STORAGE_KEY);
    return saved != null && saved !== '' ? saved : null;
  } catch (_) {
    return null;
  }
}

export function ZoneProvider({ children }) {
  const { user } = useAuth();
  const [selectedZone, setSelectedZone] = useState(readStoredZone);

  const updateZone = useCallback((zoneId) => {
    const next = zoneId == null || zoneId === '' ? null : String(zoneId);
    setSelectedZone(next);
    try {
      if (next == null)
        localStorage.removeItem(SELECTED_ZONE_STORAGE_KEY);
      else localStorage.setItem(SELECTED_ZONE_STORAGE_KEY, next);
    } catch (_) {
    }
  }, []);

  //Keep stored zone only if allowed for this user
  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}user/operationalZone_options`,
          { credentials: 'include' }
        );
        const data = await res.json();
        if (cancelled) return;

        const allowedIds = (data.zones || []).map((z) => String(z.id));
        const stored = readStoredZone();

        if (stored != null && allowedIds.includes(stored)) {
          updateZone(stored);
          return;
        }

        if (allowedIds.length > 0) {
          updateZone(allowedIds[0]);
          return;
        }

        const zoneRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}storage/zones/user_zone`,
          { credentials: 'include' }
        );
        const zoneData = await zoneRes.json();
        if (!cancelled && zoneData?.operationalZoneId != null) {
          updateZone(zoneData.operationalZoneId);
        } else if (!cancelled) {
          updateZone(null);
        }
      } catch (_) {
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.email, updateZone]);

  const value = useMemo(
    () => ({ selectedZone, updateZone }),
    [selectedZone, updateZone]
  );

  return <ZoneContext.Provider value={value}>{children}</ZoneContext.Provider>;
}

export function useZone() {
  const ctx = useContext(ZoneContext);
  if (!ctx) {
    throw new Error('useZone must be used within a ZoneProvider');
  }
  return ctx;
}
